import { MANAGER_COLORS, type ManagerColor } from "@salesboard/contracts";

/** `ManagerColor` key → hex. Shared by every surface that wears an owner's colour. */
export const MANAGER_HEX: Record<ManagerColor, string> = Object.fromEntries(
  MANAGER_COLORS.map((color) => [color.key, color.hex]),
) as Record<ManagerColor, string>;

/** White initials, always. See `badgeFill` for what makes that safe. */
export const BADGE_TEXT = "#ffffff";

/** WCAG AA for text. The badge's initials are small, so the large-text allowance is not ours. */
const MIN_CONTRAST = 4.5;

/**
 * The fill for an initials circle: the owner's colour, darkened until **white** text on it
 * clears WCAG AA.
 *
 * This replaces choosing black *or* white per hue (2026-08-25). That version picked whichever
 * had the higher measured ratio, which on a saturated mid-tone like emerald chose black — and
 * black initials on green read as a mistake even though the arithmetic passed, because at 9px
 * semibold the strokes are thin and the hue is bright. It also meant the badges as a set had
 * no consistent rule: two colours side by side with opposite text made the palette look
 * accidental, which is precisely the report this fixes.
 *
 * So the text is fixed and the *background* moves: mix the hue toward black in small steps
 * until white passes. Every palette entry keeps its identity — a darkened emerald is still
 * unmistakably the emerald one, and the card's own soft tint is untouched by this — while the
 * initials are legible by construction rather than by luck. `manager-colors.test.ts` asserts
 * the guarantee for all nine keys.
 */
export function badgeFill(hex: string): string {
  let mix = 0;
  // 5% steps: fine enough that no colour is darkened further than it needs, coarse enough to
  // terminate immediately. Pure black would satisfy any threshold, hence the guard rail.
  while (mix < 0.75 && contrastWithWhite(darken(hex, mix)) < MIN_CONTRAST) {
    mix += 0.05;
  }
  return darken(hex, mix);
}

function contrastWithWhite(hex: string): number {
  return 1.05 / (relativeLuminance(hex) + 0.05);
}

/** Mix toward black in sRGB. Not oklab: this is a fill, not a `color-mix` the browser performs. */
function darken(hex: string, amount: number): string {
  const value = Number.parseInt(hex.slice(1), 16);
  const channels = [(value >> 16) & 255, (value >> 8) & 255, value & 255].map((channel) =>
    Math.round(channel * (1 - amount)),
  );
  return `#${channels.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
}

function relativeLuminance(hex: string): number {
  const value = Number.parseInt(hex.slice(1), 16);
  const [r, g, b] = [((value >> 16) & 255) / 255, ((value >> 8) & 255) / 255, (value & 255) / 255].map(toLinear);
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
}

function toLinear(channel: number): number {
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}
